/**
 * useMarkItem — Hook for marking items in the 3D viewer.
 * Extracts mark-item logic from NewModeViewer (following sealed file rules).
 */
import { useCallback, useState } from 'react'
import { useCartStore } from '@/stores/cartStore'
import { useAppStore } from '@/store/appStore'
import { cropItemThumbnail } from '@/features/shopping/services/imageService'
import type { MarkedItem } from '@/features/delivery/types'

interface MarkItemParams {
  shopId: string
}

export function useMarkItem({ shopId }: MarkItemParams) {
  const [itemListOpen, setItemListOpen] = useState(false)
  const [cartDrawerOpen, setCartDrawerOpen] = useState(false)

  const addMarkedItem = useCartStore((s) => s.addMarkedItem)
  const cartItemCount = useCartStore((s) => s.cartItemCount)

  /**
   * Mark the currently tapped item.
   * Called when user clicks the shopping bag button on TapCameraIndicator.
   */
  const handleMarkItem = useCallback((opts: {
    imagePixelX: number
    imagePixelY: number
    radius: number
    point3D: [number, number, number] | null
    projections: Map<number, [number, number]>
    frameIndex: number
  }) => {
    const currentSectionId = useAppStore.getState().currentSectionId

    // Convert projections Map to Record for serialization
    const projectionsRecord: Record<number, [number, number]> = {}
    if (opts.projections) {
      opts.projections.forEach((val, key) => {
        projectionsRecord[key] = val
      })
    }

    const itemId = `item_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`

    // Create item immediately (thumbnail added async)
    const item: MarkedItem = {
      id: itemId,
      shopId,
      sectionId: String(currentSectionId),
      frameIndex: opts.frameIndex,
      imagePixelX: opts.imagePixelX,
      imagePixelY: opts.imagePixelY,
      radius: opts.radius,
      point3D: opts.point3D,
      projections: projectionsRecord,
      name: '',
      price: null,
      thumbnailDataUrl: null,
      createdAt: new Date().toISOString(),
    }

    addMarkedItem(item)
    console.log(`🛍️ [useMarkItem] Marked item at frame ${opts.frameIndex}, pixel (${opts.imagePixelX.toFixed(1)}, ${opts.imagePixelY.toFixed(1)})`)

    // Async: crop thumbnail and update store
    cropItemThumbnail(opts.imagePixelX, opts.imagePixelY, opts.radius)
      .then((dataUrl) => {
        if (dataUrl) {
          // Direct mutation is fine here — Zustand picks up the reference on next read
          const storeItems = useCartStore.getState().items
          const target = storeItems.find((i) => i.id === itemId)
          if (target) {
            target.thumbnailDataUrl = dataUrl
          }
        }
      })
      .catch((e) => console.warn('[useMarkItem] Thumbnail crop failed:', e))
  }, [shopId, addMarkedItem])

  return {
    handleMarkItem,
    itemListOpen,
    setItemListOpen,
    cartDrawerOpen,
    setCartDrawerOpen,
    cartItemCount,
  }
}
